import { useState } from "react";
import { Modal, LabelChips } from "./components";

export const DEFAULT_STEPS = [
  "Planned",
  "Building",
  "Our testing",
  "Customer review",
  "Fixing feedback",
  "Customer sign-off",
  "Live",
];

// Commas only: a label typed with a space must reach the backend and be
// rejected, not be split silently into several labels.
const splitLabels = (text) =>
  text
    .split(/[,\n]+/)
    .map((label) => label.trim())
    .filter(Boolean);

const splitLines = (text) =>
  text
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

/** Create or edit a view. `view` is null when creating. */
export function ViewFormModal({ view, onClose, onSave }) {
  const [name, setName] = useState(view?.name || "");
  const [description, setDescription] = useState(view?.description || "");
  const [labels, setLabels] = useState((view?.labels || []).join(", "));
  const [labelMatch, setLabelMatch] = useState(view?.labelMatch || "all");
  const [projectKey, setProjectKey] = useState(view?.projectKey || "DEV");
  const [steps, setSteps] = useState((view?.steps || DEFAULT_STEPS).join("\n"));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      await onSave({
        name,
        description,
        labels: splitLabels(labels),
        labelMatch,
        projectKey,
        steps: splitLines(steps),
      });
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <Modal
      title={view ? "Edit view" : "New view"}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="pv-btn" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form="pv-view-form" className="pv-btn pv-btn--primary" disabled={saving}>
            {saving ? "Saving…" : view ? "Save view" : "Create view"}
          </button>
        </>
      }
    >
      <form id="pv-view-form" className="pv-form" onSubmit={submit}>
        <label>
          <span>Name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Newfi" />
        </label>
        <label>
          <span>Description</span>
          <input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Everything we're working on for Newfi"
          />
        </label>
        <label>
          <span>Jira labels</span>
          <input value={labels} onChange={(e) => setLabels(e.target.value)} placeholder="newfi" />
          <small>
            Epics in {projectKey || "DEV"} carrying these labels show in the view. Separate labels with commas.
          </small>
        </label>
        <fieldset className="pv-radio">
          <legend>Match</legend>
          <label>
            <input type="radio" checked={labelMatch === "all"} onChange={() => setLabelMatch("all")} />
            Every label
          </label>
          <label>
            <input type="radio" checked={labelMatch === "any"} onChange={() => setLabelMatch("any")} />
            Any label
          </label>
        </fieldset>
        <label>
          <span>Jira project</span>
          <input value={projectKey} onChange={(e) => setProjectKey(e.target.value.toUpperCase())} />
        </label>
        <label>
          <span>Steps, one per line</span>
          <textarea rows={8} value={steps} onChange={(e) => setSteps(e.target.value)} />
          <small>
            Saved on each epic as its Project Step. Renaming a step here does not rename it on epics that already use it.
          </small>
        </label>
        {error ? <div className="pv-error">{error}</div> : null}
      </form>
    </Modal>
  );
}

/** Create a Jira epic that carries the view's labels. */
export function NewEpicModal({ view, onClose, onCreate }) {
  const [summary, setSummary] = useState("");
  const [description, setDescription] = useState("");
  const [step, setStep] = useState(view.steps[0]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      await onCreate({ summary, description, step });
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <Modal
      title="New project"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="pv-btn" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form="pv-epic-form" className="pv-btn pv-btn--primary" disabled={saving}>
            {saving ? "Creating in Jira…" : "Create epic"}
          </button>
        </>
      }
    >
      <form id="pv-epic-form" className="pv-form" onSubmit={submit}>
        <label>
          <span>Title</span>
          <input value={summary} onChange={(e) => setSummary(e.target.value)} placeholder="Scenario Center" />
        </label>
        <label>
          <span>What it is</span>
          <textarea
            rows={4}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="One or two plain sentences on what this project does."
          />
        </label>
        <label>
          <span>Step</span>
          <select value={step} onChange={(e) => setStep(e.target.value)}>
            {view.steps.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </label>
        <p className="pv-note">
          Creates an Epic in {view.projectKey} with the labels <LabelChips labels={view.labels} />
        </p>
        {error ? <div className="pv-error">{error}</div> : null}
      </form>
    </Modal>
  );
}
